import type { HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Responsive container: 16px gutters on mobile, 24 on tablet, 32 on desktop. Max 1280. */
export function Container({ className, narrow, ...rest }: HTMLAttributes<HTMLDivElement> & { narrow?: boolean }) {
  return <div className={cn('mx-auto w-full px-4 sm:px-6 lg:px-8', narrow ? 'max-w-4xl' : 'max-w-7xl', className)} {...rest} />;
}

export function Section({ className, children, ...rest }: HTMLAttributes<HTMLElement>) {
  return <section className={cn('py-8 sm:py-12', className)} {...rest}>{children}</section>;
}

export function SectionHeading({
  title, subtitle, to, linkLabel = 'View all', className,
}: { title: string; subtitle?: string; to?: string; linkLabel?: string; className?: string }) {
  return (
    <div className={cn('mb-5 flex items-end justify-between gap-4 sm:mb-7', className)}>
      <div>
        <h2 className="text-xl font-extrabold tracking-tight sm:text-2xl lg:text-3xl">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground sm:text-base">{subtitle}</p>}
      </div>
      {to && (
        <Link to={to} className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary hover:underline">
          {linkLabel} <ArrowRight className="size-4" />
        </Link>
      )}
    </div>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
        {description && <p className="mt-1 text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </div>
  );
}
